import React from 'react';
import { Formik } from 'formik';

import { DAILY_CHALLENGE_STRUCTURES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';

import { Loading } from 'components/shared/Loading';
import { Typography } from 'components/shared/Typography';

import { MainLayout } from 'components/Layout';
import { BreadcrumbsToolbar } from 'components/Toolbar';
import { AutoSave } from 'components/AutoSave';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';
import { FormikField } from 'components/FormikField';
import { InputField } from 'components/FormikFields';

import { MainSettings } from 'pages/CoursePage/components/MainSettings';
import { LessonsSettings } from 'pages/CoursePage/components/LessonsSettings';
import { ModulesSettings } from 'pages/CoursePage/components/ModulesSettings';
import { StructureSelect } from 'pages/CoursePage/components/StructureSelect';
import { CourseResultPreview } from 'pages/CoursePage/components/CourseResultPreview';

import styles from 'pages/CoursePage/CoursePage.module.scss';

import {
  convertDataToForm,
  getContextItems,
  getReadonlyTrainingText,
} from './DailyChallengePage.helpers';

import { moreContextItems } from './DailyChallengePage.config';

import moduleStyles from './DailyChallengePage.module.scss';

const DailyChallengePage = ({
  loading,
  isRootAdmin,
  saveLoading,
  autoSaveLoading,
  readOnly,
  dailyChallenge,
  lastActionAt,
  onAutoSaveSubmit,
  onSaveClick,
  onPreviewClick,
  onStatusContextItemClick,
  onMoreContextItemClick,
}: {
  loading: any;
  isRootAdmin: any;
  saveLoading: any;
  autoSaveLoading: any;
  readOnly: any;
  dailyChallenge: any;
  lastActionAt: any;
  onAutoSaveSubmit: any;
  onSaveClick: any;
  onPreviewClick: any;
  onStatusContextItemClick: any;
  onMoreContextItemClick: any;
}) => (
  <Formik
    enableReinitialize
    validateOnMount
    initialValues={convertDataToForm(dailyChallenge)}
    onSubmit={onAutoSaveSubmit}
  >
    {({ values, setFieldValue }: { values: any; setFieldValue: any }) =>
      loading ? (
        <Loading />
      ) : (
        <MainLayout
          toolbarComponent={
            <BreadcrumbsToolbar
              breadcrumbsData={{
                courseTitle:
                  values.title || getTranslation('common.text.untitled'),
              }}
              endAdornment={
                <ToolbarActions
                  saveLoading={saveLoading}
                  isRootAdmin={isRootAdmin}
                  autoSaveLoading={autoSaveLoading}
                  contextItems={getContextItems(dailyChallenge.status)}
                  moreContextItems={moreContextItems}
                  updatedAt={lastActionAt}
                  status={dailyChallenge.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onStatusContextItemClick={(item: any) =>
                    onStatusContextItemClick(item, { values, setFieldValue })
                  }
                  onMoreContextItemClick={onMoreContextItemClick}
                />
              }
            />
          }
        >
          <div className={styles.page}>
            <AutoSave debounceMs={1000} />
            {readOnly && (
              <ReadOnlySection
                text={getReadonlyTrainingText(dailyChallenge.status)}
              />
            )}
            <MainSettings type={dailyChallenge.type} />
            <div className={moduleStyles.scheduledDay}>
              <Typography
                className={moduleStyles.scheduledDayTitle}
                variant="body2"
                weight="medium"
                mode="compact"
              >
                Scheduled day (UTC)
              </Typography>
              <FormikField
                name="scheduledDay"
                component={InputField}
                componentProps={{
                  type: 'date',
                  readOnly: values.readOnly,
                }}
              />
            </div>
            <CourseResultPreview type={dailyChallenge.type} />
            {!(values as Record<string, unknown>).structure && !readOnly && (
              <StructureSelect />
            )}
            {(values as Record<string, unknown>).structure ===
              DAILY_CHALLENGE_STRUCTURES.MODULES && <ModulesSettings />}
            {(values as Record<string, unknown>).structure ===
              DAILY_CHALLENGE_STRUCTURES.LESSONS && <LessonsSettings />}
          </div>
        </MainLayout>
      )
    }
  </Formik>
);

export default React.memo(DailyChallengePage) as any;
